import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgSelectModule } from '@ng-select/ng-select';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ApiService } from 'src/app/services/api.service';
import { SubContractService } from 'src/app/services/sub-contract.service';
import { JobModel } from 'src/app/services/jobModel';
import { NG_ICON_DIRECTIVES } from '@ng-icons/core';


const RATE_TYPES = [
  { value: 'HOURLY', label: '/hr' },
  { value: 'DAILY', label: '/day' },
  { value: 'WEEKLY', label: '/wk' },
  { value: 'MONTHLY', label: '/mo' },
  { value: 'FIXED', label: 'Fixed' }
];

@Component({
  selector: 'app-sub-contract-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule,
    NG_ICON_DIRECTIVES,
  ],
  templateUrl: './sub-contract-modal.component.html',
  styleUrls: ['./sub-contract-modal.component.scss']
})
export class SubContractModalComponent implements OnInit {
  jobDetails: JobModel;
  businessId: string = '';
  userId: string = '';

  networks: any[] = [];
  selectedNetworks: any[] = [];
  lockedNetworkIds: Set<string> = new Set();
  disabledNetworkTypes: Set<string> = new Set();

  rateFrom: number | null = null;
  rateTo: number | null = null;
  rateType: string = '';

  loading = false;
  submitting = false;
  submitted = false;

  readonly rateTypes = RATE_TYPES;

  get isEditMode(): boolean {
    return this.jobDetails?.subContractStatus === 'PUBLISHED';
  }

  get hasClientRate(): boolean {
    return !!this.jobDetails?.targetRateFrom && !!this.jobDetails?.targetRateTo;
  }

  get currencySymbol(): string {
    switch (this.jobDetails?.country) {
      case 'US': return '$';
      case 'IN': return '₹';
      case 'CA': return 'C$';
      case 'AU': return 'A$';
      case 'GB': return '£';
      default: return '$';
    }
  }

  constructor(
    public bsModalRef: BsModalRef,
    private api: ApiService,
    private subContractService: SubContractService
  ) {}

  ngOnInit(): void {
    this.rateType = this.jobDetails?.payType?.toUpperCase() || '';
    this.loading = true;
    forkJoin({
      networks: this.api.create('listvalue/findbyList', {
        domain: 'ENGAGEMENT_TYPE,JOB_ENGAGEMENT_TYPES,CLIENT_TYPE,JOB_CLIENT_TYPES,PAY_TYPE,DURATION,PRIMARY_SKILLS,WORK_AUTHORIZATION,CANDIDATE_AVAILABLITY,TOTAL_EXPERIENCE,SECONDARY_SKILLS,GIGSUMO_SKILLS_LIST,GIGSUMO_JOB_TITLE,GIGSUMO_RECRUITERS_TITLE_LIST',
        type: 'JOB',
        userId: localStorage.getItem('userId')
      }),
      settings: this.api.query(`business/getNetworkPermissions?businessId=${this.businessId}`).pipe(catchError(() => of(null))),
      existingDetails: this.isEditMode
        ? this.subContractService.getSubContractDetails(this.jobDetails.jobId, this.businessId).pipe(catchError(() => of(null)))
        : of(null)
    }).subscribe({
      next: ({ networks, settings, existingDetails }: any) => {
        this.loading = false;
        const perms = settings?.data?.networkPermissions;
        this.disabledNetworkTypes.clear();
        if (perms) {
          if (perms.personalNetworks === false) this.disabledNetworkTypes.add('Personal Networks');
          if (perms.memberNetworks === false) this.disabledNetworkTypes.add('Member Networks');
          if (perms.b2bNetworks === false) this.disabledNetworkTypes.add('Business Networks');
        }

        if (networks?.code === '00000') {
          const data = networks.data;
          const baseNetworks: any[] = [
            ...(data.businessNetworks || []).map((n: any) => ({ id: n.b2bNetworkId, name: n.b2bNetworkName, type: 'Business Networks' })),
            ...(data.memberNetworks || []).map((n: any) => ({ id: n.businessNetworkId, name: n.businessNetworkName, type: 'Member Networks' })),
            ...(data.personalNetworks || []).map((n: any) => ({ id: n.networkId, name: n.networkName, type: 'Personal Networks' }))
          ];
          // Marketplace is not available for sub-contracting
          this.networks = [...baseNetworks];
        }

        // Org-level locked networks (Day 1 of publish schedule, split-array format)
        const schedule: any[] = settings?.data?.publishSchedule || [];
        const day1Step = schedule.find((s: any) => s.day === 1);
        const day1Ids: string[] = day1Step
          ? [
              ...((day1Step.b2bNetworkIds as string[]) || []),
              ...((day1Step.personalNetworkIds as string[]) || []),
              ...((day1Step.memberNetworkIds as string[]) || [])
            ]
          : [];
        this.lockedNetworkIds.clear();
        day1Ids.forEach((id: string) => this.lockedNetworkIds.add(id));

        this.networks = this.networks.map(n => ({
          ...n,
          disabled: this.disabledNetworkTypes.has(n.type)
        }));

        if (this.isEditMode && existingDetails?.data) {
          // Use data from GET /sub-contract/details API
          const details = existingDetails.data;

          this.rateFrom = details.fromPayRate || null;
          this.rateTo = details.toPayRate || null;
          if (details.rateType) this.rateType = details.rateType;

          // Pre-select networks from existing published data
          const existingIds = new Set<string>([
            ...(details.b2bNetworkIds || []),
            ...(details.personalNetworkIds || []),
            ...(details.memberNetworkIds || [])
          ]);
          this.selectedNetworks = this.networks.filter(n => existingIds.has(n.id));
        } else if (this.isEditMode) {
          // Fallback: try from jobDetails if API failed
          const jd = this.jobDetails as any;
          this.rateFrom = jd?.rateFrom ?? jd?.fromPayRate ?? null;
          this.rateTo = jd?.rateTo ?? jd?.toPayRate ?? null;
          if (jd?.rateType) this.rateType = jd.rateType;
        } else {
          // Pre-select locked networks
          this.selectedNetworks = this.networks.filter(n => this.lockedNetworkIds.has(n.id));
        }
      },
      error: () => { this.loading = false; }
    });
  }

  isLocked(network: any): boolean {
    return this.lockedNetworkIds.has(network.id);
  }

  isNetworkDisabledByPermission(network: any): boolean {
    if (!network) return false;
    return this.disabledNetworkTypes.has(network.type);
  }

  submit(): void {
    this.submitted = true;
    if (!this.selectedNetworks.length || !this.rateFrom || this.rateFrom <= 0 || !this.rateTo || this.rateTo <= 0 || !this.rateType) {
      return;
    }

    this.submitting = true;
    const b2bNetworkIds: string[] = [];
    const personalNetworkIds: string[] = [];
    const memberNetworkIds: string[] = [];
    this.selectedNetworks.forEach(n => {
      if (n.type === 'Business Networks') b2bNetworkIds.push(n.id);
      else if (n.type === 'Personal Networks') personalNetworkIds.push(n.id);
      else if (n.type === 'Member Networks') memberNetworkIds.push(n.id);
    });

    const basePayload = {
      jobId: this.jobDetails.jobId,
      businessId: this.businessId,
      rateFrom: this.rateFrom,
      rateTo: this.rateTo || this.rateFrom,
      rateType: this.rateType,
      b2bNetworkIds,
      personalNetworkIds,
      memberNetworkIds
    };
    const call$ = this.isEditMode
      ? this.subContractService.updateSubContract(basePayload)
      : this.subContractService.publishSubContract({ ...basePayload, subContractedBy: this.userId });

    call$.subscribe({
      next: (res: any) => {
        this.submitting = false;
        if (res.code === '00000') {
          Swal.fire({ icon: 'success', showConfirmButton: false, timer: 2000, title: (this.isEditMode ? 'Updated' : 'Published') + ' Successfully' });
          this.jobDetails.subContractStatus = 'PUBLISHED';
          this.bsModalRef.hide();
        } else {
          Swal.fire({ icon: 'error', text: res.message || 'Something went wrong.', showConfirmButton: true });
        }
      },
      error: () => {
        this.submitting = false;
        Swal.fire({ icon: 'error', text: 'Something went wrong. Please try again.', showConfirmButton: true });
      }
    });
  }

  cancel(): void {
    this.bsModalRef.hide();
  }
}
